/**
 * Clubbed "payslips" income source.
 *
 * Payslip items come from tenantExtraData.forensic_payslip_items (built in the
 * backend, every payslip with a stable id). Landlords choose which items are
 * clubbed; the choice is stored in sharedProfile.income_selection:
 *   income_selection[tenantId].tenant_components.payslips = [ids]
 *   income_selection[tenantId].guarantor_components[gid].payslips = [ids]
 * The clubbed amount (plain sum) counts as ONE source ("payslips") in
 * getCustomIncomeDetails.
 *
 * Kept free of imports from ./common to avoid a circular dependency.
 */

export const PAYSLIPS_SOURCE = "payslips";

// ---- Sum / Mean of the selected items of a component ----
// Saved as the "componentModes" component: ["payslips:avg", "tax:sum", …].
// A component without an entry uses sum (the previous behaviour).
export const INCOME_MODE_SUM = "sum";
export const INCOME_MODE_AVG = "avg";
export const MODE_COMPONENTS = ["payslips", "tax", "otherIncomes"];

// ["payslips:avg"] -> { payslips: "avg", tax: "sum", otherIncomes: "sum" }
export const parseComponentModes = (entries = []) => {
  const modes = Object.fromEntries(
    MODE_COMPONENTS.map((c) => [c, INCOME_MODE_SUM])
  );
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    if (typeof entry !== "string") return;
    const [component, mode] = entry.split(":");
    if (
      MODE_COMPONENTS.includes(component) &&
      [INCOME_MODE_SUM, INCOME_MODE_AVG].includes(mode)
    )
      modes[component] = mode;
  });
  return modes;
};

// entries with the mode of one component replaced
export const setComponentMode = (entries = [], component, mode) => [
  ...(Array.isArray(entries) ? entries : []).filter(
    (e) => typeof e === "string" && !e.startsWith(`${component}:`)
  ),
  `${component}:${mode}`,
];

// total of `count` selected items: the sum, or its mean (whole euros)
export const applyIncomeMode = (total, count, mode = INCOME_MODE_SUM) =>
  mode === INCOME_MODE_AVG && count > 1 ? Math.round(total / count) : total;
const LEGACY_PAYSLIP_PREFIX = "payslip:";

export const isLegacyPayslipSource = (source) =>
  typeof source === "string" && source.startsWith(LEGACY_PAYSLIP_PREFIX);

// same as getUpdatedExchangeIncome in ./common
const toEur = (amount, currency, originalUser) => {
  const rate = originalUser?.common?.currency_exchange?.rates?.[currency] ?? 1;
  return Math.round(amount * rate);
};

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

// "September 2026" -> "2026-09"
const legacyLabelToMonth = (label = "") => {
  const [monthName, year] = label.trim().split(" ");
  const idx = MONTHS.indexOf(monthName?.toLowerCase());
  return idx === -1 || !year
    ? null
    : `${year}-${String(idx + 1).padStart(2, "0")}`;
};

/**
 * Payslip items for a user (tenant or guarantor), newest first.
 * Falls back to the legacy `payslip:<Month YYYY>:<Employer>` keys for users
 * whose forensic data has not been re-extracted yet (id = legacy key).
 */
export const getPayslipItems = (user) => {
  const extra = user?.tenant_extra_field_id;
  // empty = not extracted yet (Mongoose returns [] for unset array fields)
  if (
    Array.isArray(extra?.forensic_payslip_items) &&
    extra.forensic_payslip_items.length > 0
  ) {
    return extra.forensic_payslip_items;
  }

  const financial = extra?.forensic_financial_data ?? {};
  return Object.entries(financial)
    .filter(([key]) => isLegacyPayslipSource(key))
    .map(([key, value]) => {
      const [, label, employer = ""] = key.split(":");
      const month = legacyLabelToMonth(label);
      return {
        id: key,
        legacy_key: key,
        is_legacy: true,
        date: month ? `${month}-01` : null,
        month,
        employer,
        amount: value?.amount ?? 0,
        currency: value?.currency ?? null,
        origin: value?.origin ?? null,
        verified: Boolean(value?.verified),
      };
    })
    .sort((a, b) => ((a.month ?? "") < (b.month ?? "") ? 1 : -1));
};

// [{ employer, payslips }] — employers ordered by their latest payslip.
export const groupPayslipsByEmployer = (items = []) => {
  const byEmployer = {};
  for (const item of items) {
    const key = item?.employer ?? "";
    if (!byEmployer[key]) byEmployer[key] = [];
    byEmployer[key].push(item);
  }
  // items are newest first, so insertion order is already correct
  return Object.entries(byEmployer).map(([employer, payslips]) => ({
    employer,
    payslips,
  }));
};

// Default: every payslip in the latest month across all employers.
export const getDefaultPayslipIds = (items = []) => {
  const months = items
    .map((i) => i?.month)
    .filter(Boolean)
    .sort();
  const latest = months.at(-1);
  return latest ? items.filter((i) => i.month === latest).map((i) => i.id) : [];
};

/**
 * Saved clubbed payslip ids for a user. `selection` may be the raw
 * sharedProfile.income_selection (keyed by tenant id), the per-tenant entry,
 * or the output of resolveIncomeSelection. null = nothing saved.
 */
export const getSavedPayslipIds = (
  selection,
  { tenantId, userId, isGuarantor = false } = {}
) => {
  const candidates = [tenantId ? selection?.[tenantId] : null, selection];
  for (const entry of candidates) {
    const saved = isGuarantor
      ? entry?.guarantor_components?.[userId]?.payslips
      : entry?.tenant_components?.payslips;
    if (Array.isArray(saved)) return saved;
  }
  return null;
};

/**
 * Which payslip ids are clubbed:
 * 1. saved ids (stale ids dropped)
 * 2. legacy `payslip:*` sources in the saved source list (read-time conversion)
 * 3. default (latest month)
 */
export const resolvePayslipIds = (
  items = [],
  savedIds = null,
  sources = []
) => {
  if (Array.isArray(savedIds)) {
    const saved = new Set(savedIds);
    // ids saved from the legacy fallback are legacy keys — map them to the
    // re-extracted items by legacy_key; other stale ids are dropped
    return items
      .filter(
        (i) =>
          saved.has(i.id) ||
          (!i.is_legacy && saved.has(i.legacy_key)) ||
          // gross-only payslip saved before re-extraction (read-time id)
          (!i.is_legacy &&
            i?.amounts?.net == null &&
            saved.has(getGrossOnlyPayslipId(i.legacy_key)))
      )
      .map((i) => i.id);
  }

  const legacyKeys = (Array.isArray(sources) ? sources : [sources]).filter(
    isLegacyPayslipSource
  );
  if (legacyKeys.length) {
    const ids = items
      .filter((i) => legacyKeys.includes(i.legacy_key))
      .map((i) => i.id);
    if (ids.length) return ids;
  }

  return getDefaultPayslipIds(items);
};

// ---- figures per payslip (net / gross) ----
// Stored as the "payslipFields" component: ["<payslip id>::<field>"].
// A selected payslip without entries uses net (the previous behaviour).
export const PAYSLIP_FIELDS = ["net", "gross"];
export const DEFAULT_PAYSLIP_FIELD = "net";
const FIELD_SEPARATOR = "::";

export const getPayslipFieldId = (payslipId, field) =>
  `${payslipId}${FIELD_SEPARATOR}${field}`;

// value of a figure on a payslip (null = not on the payslip)
export const getPayslipFieldValue = (item, field) => {
  if (field === "net") return item?.amounts?.net ?? item?.amount ?? null;
  if (field === "gross") return item?.amounts?.gross ?? null;
  return null;
};

// figures that can be shown: present and greater than 0
export const getAvailablePayslipFields = (item) =>
  PAYSLIP_FIELDS.filter((f) => {
    const v = Number(getPayslipFieldValue(item, f));
    return Number.isFinite(v) && v > 0;
  });

// "id::field" entries -> { id: [fields] }
export const parsePayslipFieldEntries = (entries = []) => {
  const map = {};
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    if (typeof entry !== "string") return;
    const idx = entry.lastIndexOf(FIELD_SEPARATOR);
    if (idx === -1) return;
    const id = entry.slice(0, idx);
    const field = entry.slice(idx + FIELD_SEPARATOR.length);
    if (!PAYSLIP_FIELDS.includes(field)) return;
    if (!map[id]) map[id] = [];
    if (!map[id].includes(field)) map[id].push(field);
  });
  return map;
};

// figures used for one payslip: saved ones that exist, else net
export const getPayslipFieldsFor = (item, fieldMap = {}) => {
  const available = getAvailablePayslipFields(item);
  const saved = (fieldMap?.[item?.id] ?? []).filter((f) =>
    available.includes(f)
  );
  // one figure per payslip (net OR gross); selections saved before this
  // rule may hold both, the last one chosen wins
  if (saved.length) return [saved[saved.length - 1]];
  return available.includes(DEFAULT_PAYSLIP_FIELD)
    ? [DEFAULT_PAYSLIP_FIELD]
    : available.slice(0, 1);
};

// amount of one payslip = sum of its chosen figures (original currency)
export const getPayslipItemAmount = (item, fieldMap = {}) =>
  getPayslipFieldsFor(item, fieldMap).reduce(
    (sum, f) => sum + (Number(getPayslipFieldValue(item, f)) || 0),
    0
  );

// Sum (or mean, mode "avg") of the clubbed payslips (chosen figures), in EUR.
export const getPayslipGroupAmount = (
  items = [],
  ids = [],
  originalUser,
  fieldEntries = null,
  mode = INCOME_MODE_SUM
) => {
  const selected = new Set(ids);
  const fieldMap = parsePayslipFieldEntries(fieldEntries ?? []);
  const chosen = items.filter((i) => selected.has(i.id));
  const total = chosen.reduce(
    (sum, i) =>
      sum +
      (toEur(getPayslipItemAmount(i, fieldMap), i.currency, originalUser) || 0),
    0
  );
  return applyIncomeMode(total, chosen.length, mode);
};

/**
 * Replaces legacy `payslip:*` entries with the single "payslips" source.
 * Only applied when the user has payslip items.
 */
export const normalizePayslipSources = (sources = [], hasItems = true) => {
  const list = Array.isArray(sources) ? sources : [sources];
  if (!hasItems || !list.some(isLegacyPayslipSource)) return list;
  const withoutLegacy = list.filter((s) => !isLegacyPayslipSource(s));
  return withoutLegacy.includes(PAYSLIPS_SOURCE)
    ? withoutLegacy
    : [PAYSLIPS_SOURCE, ...withoutLegacy];
};

/**
 * When the "payslips" source is saved from UpdateIncomeSourceModal and no
 * clubbed ids were saved yet, returns the ids currently in effect (legacy
 * `payslip:*` conversion or default) so they can be persisted and the
 * effective amount doesn't change silently. null = nothing to persist.
 */
export const getPayslipIdsToPersist = ({
  selection,
  tenantId,
  targetUser,
  userId,
  isGuarantor = false,
}) => {
  if (
    getSavedPayslipIds(selection, { tenantId, userId, isGuarantor }) !== null
  ) {
    return null;
  }
  const items = getPayslipItems(targetUser);
  if (!items.length) return null;

  const entry = selection?.[tenantId] ?? selection;
  const rawSources = isGuarantor
    ? (entry?.guarantor_sources?.[userId] ??
      selection?.guarantor_sources?.[userId])
    : (entry?.tenant_source ?? selection?.tenant_source);

  return resolvePayslipIds(items, null, rawSources ?? []);
};

/**
 * All saved clubbed components for a user: { payslips, tax, otherIncomes } (each an id
 * array, or null = not saved). Same `selection` shapes as getSavedPayslipIds.
 * Passed as the last argument of getCustomIncomeDetails.
 */
export const getSavedIncomeComponents = (
  selection,
  { tenantId, userId, isGuarantor = false } = {}
) => {
  const read = (component) => {
    const candidates = [tenantId ? selection?.[tenantId] : null, selection];
    for (const entry of candidates) {
      const saved = isGuarantor
        ? entry?.guarantor_components?.[userId]?.[component]
        : entry?.tenant_components?.[component];
      if (Array.isArray(saved)) return saved;
    }
    return null;
  };
  return {
    payslips: read("payslips"),
    payslipFields: read("payslipFields"),
    tax: read("tax"),
    otherIncomes: read("otherIncomes"),
    componentModes: read("componentModes"),
  };
};

/**
 * Raw forensic payslip for an item. Matched by the month/employer label
 * (legacy_key) + net amount — the same local-time label on both sides, so it
 * does not depend on timezone parsing of dates like "August 2026".
 */
export const findRawPayslip = (item, rawPayslips = []) => {
  const candidates = (Array.isArray(rawPayslips) ? rawPayslips : []).filter(
    (p) => {
      const d = new Date(p?.date);
      if (!p?.date || Number.isNaN(d.getTime())) return false;
      const label = d.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
      const employer = p?.employerName ?? "";
      const key = employer
        ? `payslip:${label}:${employer}`
        : `payslip:${label}`;
      return item?.legacy_key === key;
    }
  );
  const net = item?.amounts?.net ?? item?.amount;
  const gross = item?.amounts?.gross;
  return (
    (net != null
      ? candidates.find(
          (p) => Math.round(Number(p?.netIncome)) === Math.round(net)
        )
      : null) ??
    // gross-only payslip: match the raw payslip without net, on gross
    (gross != null
      ? candidates.find(
          (p) =>
            !isPayslipAmount(p?.netIncome) &&
            Math.round(Number(p?.grossIncome)) === Math.round(gross)
        )
      : null) ??
    candidates[0] ??
    null
  );
};

const isPayslipAmount = (v) => typeof v === "number" && Number.isFinite(v);

// month/employer label of a raw forensic payslip (the legacy key format)
const getRawPayslipLegacyKey = (p) => {
  const d = new Date(p?.date);
  if (!p?.date || Number.isNaN(d.getTime())) return null;
  const label = d.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
  const employer = p?.employerName ?? "";
  return employer ? `payslip:${label}:${employer}` : `payslip:${label}`;
};

// id of a gross-only payslip added on read (before re-extraction)
export const getGrossOnlyPayslipId = (legacyKey) => `${legacyKey}#gross`;

/**
 * Payslips with a gross figure but no net are stored by the backend
 * (buildPayslipItems). For users extracted before that, or who only have the
 * legacy `payslip:*` keys, they are added here from the raw forensic payslips
 * so they show too — with gross only. Not added when already stored.
 */
export const withGrossOnlyPayslips = (items = [], rawPayslips = []) => {
  const raws = Array.isArray(rawPayslips) ? rawPayslips : [];
  // employer + month labels that already have a payslip with net: a
  // gross-only entry there is the same payslip read twice (same rule as
  // buildPayslipItems in the backend)
  const labelsWithNet = new Set(
    raws
      .filter((p) => isPayslipAmount(p?.netIncome))
      .map(getRawPayslipLegacyKey)
      .filter(Boolean)
  );
  const seen = {};
  const added = raws
    .filter(
      (p) => !isPayslipAmount(p?.netIncome) && isPayslipAmount(p?.grossIncome)
    )
    .map((p) => {
      const legacyKey = getRawPayslipLegacyKey(p);
      if (!legacyKey || labelsWithNet.has(legacyKey)) return null;
      const gross = Math.round(p.grossIncome);
      const alreadyStored = items.some(
        (i) =>
          i?.legacy_key === legacyKey &&
          i?.amounts?.net == null &&
          Math.round(Number(i?.amounts?.gross)) === gross
      );
      if (alreadyStored) return null;
      const month = legacyLabelToMonth(legacyKey.split(":")[1] ?? "");
      const baseId = getGrossOnlyPayslipId(legacyKey);
      seen[baseId] = (seen[baseId] ?? 0) + 1;
      return {
        id: seen[baseId] > 1 ? `${baseId}${seen[baseId]}` : baseId,
        legacy_key: legacyKey,
        is_legacy: true,
        date: month ? `${month}-01` : null,
        month,
        employer: p?.employerName ?? "",
        amount: null,
        amounts: { net: null, gross },
        document_url:
          p?.foundInDocuments?.find?.((d) => d?.file_url)?.file_url ?? null,
        currency: p?.currencyCode ?? null,
        origin: p?.payslipOrigin ?? null,
        verified: false,
      };
    })
    .filter(Boolean);
  if (!added.length) return items;
  // newest first, as getPayslipItems returns them
  return [...items, ...added].sort((a, b) =>
    (a.month ?? "") < (b.month ?? "") ? 1 : -1
  );
};

/**
 * Fills amounts { net, gross } (and document link) for items stored before
 * gross was added, or legacy items, from the raw forensic payslips.
 */
export const withPayslipAmounts = (items = [], rawPayslips = []) =>
  items.map((item) => {
    if (item?.amounts && item.amounts.gross !== undefined) return item;
    const raw = findRawPayslip(item, rawPayslips);
    const gross = Number(raw?.grossIncome);
    return {
      ...item,
      amounts: {
        net: item?.amounts?.net ?? item?.amount ?? null,
        gross: Number.isFinite(gross) ? Math.round(gross) : null,
      },
      document_url:
        item?.document_url ??
        raw?.foundInDocuments?.find?.((d) => d?.file_url)?.file_url ??
        null,
    };
  });
